import React, { useEffect, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  CircularProgress,
  Divider,
  FormControl,
  FormHelperText,
  InputLabel,
  MenuItem,
  Select,
  Stack,
  TextField,
  Typography,
} from '@mui/material';
import { Cancel, Info } from '@mui/icons-material';
import childEditService, {
  type ChildFullProfile,
  type ChildPersonalInfoSummary,
  type UpdateChildSchoolRequest,
} from '../services/childEditService';
import {
  scottishSchoolService,
  type ScottishCouncil,
  type ScottishSchool,
} from '../services/scottishSchoolService';

const UNKNOWN_COUNCIL = '__UNKNOWN_COUNCIL__';
const SCHOOL_NOT_LISTED = '__SCHOOL_NOT_LISTED__';

const SCHOOL_TYPES = [
  { value: 'PRIMARY', label: 'Primary School' },
  { value: 'SECONDARY', label: 'Secondary School' },
];

const GRADE_YEARS: Record<string, string[]> = {
  PRIMARY: ['P1', 'P2', 'P3', 'P4', 'P5', 'P6', 'P7'],
  SECONDARY: ['S1', 'S2', 'S3', 'S4', 'S5', 'S6'],
};

interface Props {
  childId: number;
  onClose: () => void;
  onSaved?: () => void;
}

const ChildEditPanel: React.FC<Props> = ({ childId, onClose, onSaved }) => {
  const [profile, setProfile] = useState<ChildFullProfile | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const schoolAutoMatchedRef = React.useRef(false);

  // Personal info
  const [firstName, setFirstName] = useState('');
  const [lastName, setLastName] = useState('');
  const [email, setEmail] = useState('');
  const [phoneNumber, setPhoneNumber] = useState('');
  const [birthYear, setBirthYear] = useState<number | ''>('');
  const [personalSaving, setPersonalSaving] = useState(false);
  const [personalError, setPersonalError] = useState<string | null>(null);
  const [personalSuccess, setPersonalSuccess] = useState<string | null>(null);
  const [pendingEmailInfo, setPendingEmailInfo] = useState<ChildPersonalInfoSummary | null>(null);
  const [cancellingEmail, setCancellingEmail] = useState(false);

  // School details
  const [country, setCountry] = useState('scotland');
  const [councilId, setCouncilId] = useState('');
  const [councilName, setCouncilName] = useState('');
  const [schoolType, setSchoolType] = useState('');
  const [gradeYear, setGradeYear] = useState('');
  const [schoolId, setSchoolId] = useState('');
  const [schoolName, setSchoolName] = useState('');
  const [unknownCouncil, setUnknownCouncil] = useState(false);
  const [unknownCouncilName, setUnknownCouncilName] = useState('');
  const [unknownSchool, setUnknownSchool] = useState(false);
  const [councils, setCouncils] = useState<ScottishCouncil[]>([]);
  const [schools, setSchools] = useState<ScottishSchool[]>([]);
  const [loadingCouncils, setLoadingCouncils] = useState(false);
  const [loadingSchools, setLoadingSchools] = useState(false);
  const [schoolSaving, setSchoolSaving] = useState(false);
  const [schoolError, setSchoolError] = useState<string | null>(null);
  const [schoolSuccess, setSchoolSuccess] = useState<string | null>(null);

  // Change password
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [passwordSaving, setPasswordSaving] = useState(false);
  const [passwordError, setPasswordError] = useState<string | null>(null);
  const [passwordSuccess, setPasswordSuccess] = useState<string | null>(null);

  const isPending = profile?.status === 'PENDING';

  useEffect(() => {
    schoolAutoMatchedRef.current = false;
    setLoading(true);
    childEditService.getChildProfile(childId)
      .then(p => {
        setProfile(p);
        setFirstName(p.firstName || '');
        setLastName(p.lastName || '');
        setEmail(p.email || '');
        setPhoneNumber(p.phoneNumber || '');
        setBirthYear(p.birthYear ?? '');
        setSchoolName(p.schoolName || '');
        if (p.gradeLevel) {
          setSchoolType(p.gradeLevel.startsWith('P') ? 'PRIMARY' : 'SECONDARY');
          if (/^[PS]\d$/.test(p.gradeLevel)) setGradeYear(p.gradeLevel);
        }
        if (p.councilId) {
          setCouncilId(String(p.councilId));
          setCouncilName(p.councilName || '');
        } else if (p.councilName) {
          // Council was saved as free-text (unknown council)
          setUnknownCouncil(true);
          setUnknownCouncilName(p.councilName);
        }
        if (p.pendingEmail) {
          setPendingEmailInfo({ id: p.id, firstName: p.firstName, lastName: p.lastName,
            phoneNumber: p.phoneNumber, birthYear: p.birthYear,
            activeEmail: p.email, pendingEmail: p.pendingEmail, emailChangePending: true });
        }
        setError(null);
      })
      .catch(e => setError(e.message || 'Failed to load child profile'))
      .finally(() => setLoading(false));
  }, [childId]);

  useEffect(() => {
    if (country !== 'scotland') { setCouncils([]); return; }
    setLoadingCouncils(true);
    scottishSchoolService.getCouncils()
      .then(setCouncils).catch(() => setCouncils([]))
      .finally(() => setLoadingCouncils(false));
  }, [country]);

  useEffect(() => {
    if (!councilId || !schoolType || unknownCouncil) { setSchools([]); return; }
    setLoadingSchools(true);
    scottishSchoolService.getSchools(Number(councilId), schoolType)
      .then(setSchools).catch(() => setSchools([]))
      .finally(() => setLoadingSchools(false));
  }, [councilId, schoolType, unknownCouncil]);

  // When schools load for the first time, try to pre-select the saved school by name.
  useEffect(() => {
    if (schools.length === 0 || schoolAutoMatchedRef.current) return;
    const savedName = profile?.schoolName;
    if (!savedName) return;
    schoolAutoMatchedRef.current = true;
    const match = schools.find(s => s.name === savedName);
    if (match) {
      setSchoolId(String(match.id));
      setSchoolName(match.name);
    } else {
      // School not in the dropdown list — show as custom entry
      setUnknownSchool(true);
      setSchoolName(savedName);
    }
  }, [schools, profile]);

  const handleSavePersonalInfo = async () => {
    if (!firstName.trim() || !lastName.trim()) {
      setPersonalError('First name and last name are required.');
      return;
    }
    setPersonalSaving(true);
    setPersonalError(null);
    setPersonalSuccess(null);
    try {
      const result = await childEditService.updatePersonalInfo(childId, {
        firstName, lastName, email: email || undefined,
        phoneNumber: phoneNumber || undefined,
        birthYear: birthYear !== '' ? birthYear : undefined,
      });
      if (result.emailChangePending) {
        setPendingEmailInfo(result);
        setPersonalSuccess('Personal info updated. A verification email has been sent to the new address.');
      } else {
        setPersonalSuccess('Personal information updated successfully.');
        setPendingEmailInfo(null);
      }
      onSaved?.();
    } catch (e: any) {
      setPersonalError(e.message || 'Failed to update personal info');
    } finally {
      setPersonalSaving(false);
    }
  };

  const handleCancelPendingEmail = async () => {
    setCancellingEmail(true);
    try {
      await childEditService.cancelPendingEmail(childId);
      setPendingEmailInfo(null);
      setPersonalSuccess('Pending email change cancelled.');
    } catch (e: any) {
      setPersonalError(e.message || 'Failed to cancel pending email');
    } finally {
      setCancellingEmail(false);
    }
  };

  const handleSaveSchoolDetails = async () => {
    if (!gradeYear) {
      setSchoolError('Please select a year group (e.g. S1, P3).');
      return;
    }
    const req: UpdateChildSchoolRequest = {
      schoolName: unknownSchool ? schoolName : (schoolName || ''),
      gradeLevel: gradeYear,
      unknownSchool,
      councilId: councilId ? Number(councilId) : undefined,
      councilName: unknownCouncil ? unknownCouncilName : councilName,
      unknownCouncilName: unknownCouncil ? unknownCouncilName : undefined,
    };
    if (!req.schoolName) {
      setSchoolError('School name is required.');
      return;
    }
    setSchoolSaving(true);
    setSchoolError(null);
    setSchoolSuccess(null);
    try {
      await childEditService.updateSchoolProfile(childId, req);
      setSchoolSuccess('School details updated successfully.');
      onSaved?.();
    } catch (e: any) {
      setSchoolError(e.message || 'Failed to update school details');
    } finally {
      setSchoolSaving(false);
    }
  };

  const handleChangePassword = async () => {
    if (!newPassword || !confirmPassword) {
      setPasswordError('Both password fields are required.');
      return;
    }
    if (newPassword !== confirmPassword) {
      setPasswordError('Passwords do not match.');
      return;
    }
    setPasswordSaving(true);
    setPasswordError(null);
    setPasswordSuccess(null);
    try {
      await childEditService.changeChildPassword(childId, newPassword, confirmPassword);
      setPasswordSuccess('Password changed successfully.');
      setNewPassword('');
      setConfirmPassword('');
    } catch (e: any) {
      setPasswordError(e.message || 'Failed to change password');
    } finally {
      setPasswordSaving(false);
    }
  };

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" p={4}>
        <CircularProgress />
      </Box>
    );
  }

  if (error) {
    return <Alert severity="error">{error}</Alert>;
  }

  return (
    <Box>
      {isPending && (
        <Alert severity="info" icon={<Info />} sx={{ mb: 3 }}>
          Profile editing is available once the child has verified their email. School details and
          password changes are also locked until verification is complete.
        </Alert>
      )}

      {/* Personal Information */}
      <Typography variant="h6" gutterBottom>Personal Information</Typography>
      {pendingEmailInfo?.pendingEmail && (
        <Alert
          severity="warning"
          sx={{ mb: 2 }}
          action={
            <Button
              size="small"
              startIcon={<Cancel />}
              onClick={handleCancelPendingEmail}
              disabled={cancellingEmail}
            >
              {cancellingEmail ? 'Cancelling…' : 'Cancel change'}
            </Button>
          }
        >
          Pending email change to <strong>{pendingEmailInfo.pendingEmail}</strong> — awaiting verification.
        </Alert>
      )}
      {personalError && <Alert severity="error" sx={{ mb: 1 }}>{personalError}</Alert>}
      {personalSuccess && <Alert severity="success" sx={{ mb: 1 }}>{personalSuccess}</Alert>}
      <Stack spacing={2} sx={{ mb: 2 }}>
        <Stack direction="row" spacing={2}>
          <TextField
            label="First Name"
            value={firstName}
            onChange={e => setFirstName(e.target.value)}
            disabled={isPending}
            fullWidth
            required
          />
          <TextField
            label="Last Name"
            value={lastName}
            onChange={e => setLastName(e.target.value)}
            disabled={isPending}
            fullWidth
            required
          />
        </Stack>
        <TextField
          label="Email Address"
          type="email"
          value={email}
          onChange={e => setEmail(e.target.value)}
          disabled={isPending}
          fullWidth
          helperText={
            pendingEmailInfo?.pendingEmail
              ? `Current active email: ${pendingEmailInfo.activeEmail}`
              : undefined
          }
        />
        <TextField
          label="Phone Number"
          value={phoneNumber}
          onChange={e => setPhoneNumber(e.target.value)}
          disabled={isPending}
          fullWidth
        />
        <TextField
          label="Birth Year"
          type="number"
          value={birthYear}
          onChange={e => {
            const val = e.target.value;
            setBirthYear(val === '' ? '' : Number(val));
          }}
          disabled={isPending}
          fullWidth
          inputProps={{ min: 1990, max: new Date().getFullYear() }}
          placeholder="e.g. 2012"
        />
      </Stack>
      <Button
        variant="contained"
        onClick={handleSavePersonalInfo}
        disabled={isPending || personalSaving}
        sx={{ mb: 3 }}
      >
        {personalSaving ? <CircularProgress size={20} /> : 'Save Personal Info'}
      </Button>

      <Divider sx={{ my: 3 }} />

      {/* School Details */}
      <Typography variant="h6" gutterBottom>School Details</Typography>
      {schoolError && <Alert severity="error" sx={{ mb: 1 }}>{schoolError}</Alert>}
      {schoolSuccess && <Alert severity="success" sx={{ mb: 1 }}>{schoolSuccess}</Alert>}
      <Stack spacing={2} sx={{ mb: 2 }}>
        <FormControl fullWidth disabled={isPending}>
          <InputLabel>Country</InputLabel>
          <Select value={country} label="Country" onChange={e => setCountry(e.target.value)}>
            <MenuItem value="scotland">Scotland</MenuItem>
          </Select>
        </FormControl>
        {country === 'scotland' && (
          <>
            <FormControl fullWidth disabled={isPending || loadingCouncils}>
              <InputLabel>Council</InputLabel>
              <Select
                value={unknownCouncil ? UNKNOWN_COUNCIL : councilId}
                label="Council"
                onChange={e => {
                  const val = e.target.value;
                  if (val === UNKNOWN_COUNCIL) {
                    setUnknownCouncil(true);
                    setCouncilId('');
                    setCouncilName('');
                  } else {
                    setUnknownCouncil(false);
                    setCouncilId(val);
                    const c = councils.find(c => String(c.id) === val);
                    setCouncilName(c?.name ?? '');
                  }
                  setSchoolId('');
                  setSchoolName('');
                  setUnknownSchool(false);
                }}
              >
                {councils.map(c => (
                  <MenuItem key={c.id} value={String(c.id)}>{c.name}</MenuItem>
                ))}
                <MenuItem value={UNKNOWN_COUNCIL}>My council isn't listed</MenuItem>
              </Select>
            </FormControl>
            {unknownCouncil && (
              <TextField
                label="Council name"
                value={unknownCouncilName}
                onChange={e => setUnknownCouncilName(e.target.value)}
                disabled={isPending}
                fullWidth
                helperText="Your council will be reviewed by an admin"
              />
            )}
            <FormControl fullWidth disabled={isPending}>
              <InputLabel>School Type</InputLabel>
              <Select
                value={schoolType}
                label="School Type"
                onChange={e => {
                  setSchoolType(e.target.value);
                  setGradeYear('');
                  setSchoolId('');
                  setSchoolName('');
                  setUnknownSchool(false);
                }}
              >
                {SCHOOL_TYPES.map(t => (
                  <MenuItem key={t.value} value={t.value}>{t.label}</MenuItem>
                ))}
              </Select>
            </FormControl>
            {schoolType && (
              <FormControl fullWidth disabled={isPending}>
                <InputLabel>Year Group</InputLabel>
                <Select
                  value={gradeYear}
                  label="Year Group"
                  onChange={e => setGradeYear(e.target.value)}
                >
                  {(GRADE_YEARS[schoolType] ?? []).map(yr => (
                    <MenuItem key={yr} value={yr}>{yr}</MenuItem>
                  ))}
                </Select>
              </FormControl>
            )}
            {!unknownCouncil && councilId && schoolType && (
              <FormControl fullWidth disabled={isPending || loadingSchools}>
                <InputLabel>School</InputLabel>
                <Select
                  value={unknownSchool ? SCHOOL_NOT_LISTED : schoolId}
                  label="School"
                  onChange={e => {
                    const val = e.target.value;
                    if (val === SCHOOL_NOT_LISTED) {
                      setUnknownSchool(true);
                      setSchoolId('');
                      setSchoolName('');
                    } else {
                      setUnknownSchool(false);
                      setSchoolId(val);
                      const s = schools.find(s => String(s.id) === val);
                      setSchoolName(s?.name ?? '');
                    }
                  }}
                >
                  {schools.map(s => (
                    <MenuItem key={s.id} value={String(s.id)}>{s.name}</MenuItem>
                  ))}
                  <MenuItem value={SCHOOL_NOT_LISTED}>School not listed</MenuItem>
                </Select>
                {loadingSchools && <FormHelperText>Loading schools…</FormHelperText>}
              </FormControl>
            )}
            {(unknownSchool || unknownCouncil) && (
              <TextField
                label="School name"
                value={schoolName}
                onChange={e => setSchoolName(e.target.value)}
                disabled={isPending}
                fullWidth
                helperText="Your school will be reviewed by an admin"
              />
            )}
          </>
        )}
      </Stack>
      <Button
        variant="contained"
        onClick={handleSaveSchoolDetails}
        disabled={isPending || schoolSaving}
        sx={{ mb: 3 }}
      >
        {schoolSaving ? <CircularProgress size={20} /> : 'Save School Details'}
      </Button>

      <Divider sx={{ my: 3 }} />

      {/* Change Password */}
      <Typography variant="h6" gutterBottom>Change Password</Typography>
      {passwordError && <Alert severity="error" sx={{ mb: 1 }}>{passwordError}</Alert>}
      {passwordSuccess && <Alert severity="success" sx={{ mb: 1 }}>{passwordSuccess}</Alert>}
      <Stack spacing={2} sx={{ mb: 2 }}>
        <TextField
          label="New Password"
          type="password"
          value={newPassword}
          onChange={e => setNewPassword(e.target.value)}
          disabled={isPending}
          fullWidth
        />
        <TextField
          label="Confirm New Password"
          type="password"
          value={confirmPassword}
          onChange={e => setConfirmPassword(e.target.value)}
          disabled={isPending}
          fullWidth
        />
      </Stack>
      <Button
        variant="contained"
        color="secondary"
        onClick={handleChangePassword}
        disabled={isPending || passwordSaving}
      >
        {passwordSaving ? <CircularProgress size={20} /> : 'Change Password'}
      </Button>
    </Box>
  );
};

export default ChildEditPanel;
